import { useEffect, useRef, useState } from 'react';

export const imagePromiseFactory = ({ decode = true, crossOrigin = '' }) => src => new Promise(
  (resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onerror = (msg, source, lineno, colno, error) => {
      console.error(`There was an error loading img with src ${ src }.`);
      reject(msg || error);
    };
    if (crossOrigin) img.crossOrigin = crossOrigin;

    img.onload = () => {
      if (
        decode
        && img.decode
      ) {
        img.decode().then(() => resolve(src)).catch(reject);
      } else {
        resolve(src);
      }
    };
  });

const removeBlankArrayElements = a => a.filter(x => x);
const stringToArray = x => (Array.isArray(x) ? x : [x]);
const cache = {};

const useImage = (
  {
    srcList,
    imgPromise = imagePromiseFactory({ decode: true }),
    useSuspense = true
  }) => {
  const [, setIsLoading] = useState(true);
  const mounted = useRef(false);
  const sourceList = removeBlankArrayElements(stringToArray(srcList));
  const sourceKey = sourceList.join('');

  if (!cache[sourceKey]) {
    cache[sourceKey] = {
      isLoading: true,
      src: undefined,
      error: null
    };
  }

  const evalImg = async () => {
    const srcSet = await Promise.allSettled(sourceList.map(imgPromise));
    // if a source was found, update cache
    // when not using suspense, update state to force a rerender
    const status = srcSet.find(res => res.status === 'rejected') ? 'rejected' : 'resolved';
    cache[sourceKey] = {
      ...(cache[sourceKey] || {}),
      isLoading: false,
      status,
      src: status === 'resolved' ? srcSet.map(res => res.value).at(0) : undefined,
      error: status === 'rejected' ? srcSet.map(res => res.value || res.reason).join('') : null
    };
    if (!useSuspense && mounted.current) setIsLoading(false);
  };

  useEffect(() => {
    mounted.current = true;
    evalImg();

    return () => {
      mounted.current = false;
    };
  }, []);

  if (cache[sourceKey].status === 'rejected') {
    if (useSuspense) throw cache[sourceKey].error;
  }

  return cache[sourceKey];
};

export default useImage;
